"use client";

/**
 * Outbox — cola de mutaciones offline.
 *
 * Estados:
 *   - pending  : esperando ser drenado
 *   - sending  : en vuelo
 *   - failed   : todos los reintentos consumidos (queda visible para resolución manual)
 *   - conflict : el servidor respondió 409 (versión inválida) — bloquea drenaje
 *                de items relacionados al mismo recurso hasta que el usuario resuelva
 *
 * Reintentos: backoff exponencial (1s → 5s → 30s → 2m → 10m), máx. 8.
 */

import {
  CHANNELS,
  notify,
  reqAsPromise,
  STORES,
  txReadOnly,
  txReadWrite,
} from "./db";

export type OutboxStatus = "pending" | "sending" | "failed" | "conflict";

export type OutboxActor = {
  userId: string;
  email: string | null;
  name: string | null;
  role: string;
};

export type OutboxItem = {
  id?: number;
  /** Etiqueta de dominio: "siembras", "productores", etc. Útil para listar y resolver dependencias. */
  kind: string;
  /** Método HTTP. */
  method: "POST" | "PATCH" | "PUT" | "DELETE";
  /** URL relativa (e.g. "/api/siembras"). Puede contener un id temporal "tmp-..." que el drainer resolverá. */
  url: string;
  /** Cuerpo a enviar (para POST/PATCH/PUT). */
  body?: unknown;
  /** Headers a adjuntar. Idempotency key se guarda aquí. */
  headers?: Record<string, string>;
  /** Quién originó la acción (para auditoría). */
  actor: OutboxActor;
  /** Identificador de idempotencia. Persiste en headers["X-Idempotency-Key"] también. */
  idemKey: string;
  /** Id temporal opcional (cuando la mutación crea un recurso nuevo offline). */
  tmpId?: string;
  /** Timestamps */
  createdAt: number;
  /** Próximo momento (ms epoch) en que es legítimo intentarlo. Usado por backoff. */
  nextAttemptAt: number;
  attempts: number;
  status: OutboxStatus;
  lastError?: string;
};

const MAX_ATTEMPTS = 8;
const BACKOFFS_MS = [
  1_000,
  5_000,
  30_000,
  2 * 60_000,
  5 * 60_000,
  10 * 60_000,
  20 * 60_000,
  60 * 60_000,
];

export function backoffFor(attempts: number): number {
  return BACKOFFS_MS[Math.min(attempts, BACKOFFS_MS.length - 1)];
}

// ── API ────────────────────────────────────────────────────────────────

export async function enqueueMutation(
  item: Omit<
    OutboxItem,
    "id" | "createdAt" | "nextAttemptAt" | "attempts" | "status"
  >
): Promise<OutboxItem> {
  const now = Date.now();
  const full: OutboxItem = {
    ...item,
    createdAt: now,
    nextAttemptAt: now,
    attempts: 0,
    status: "pending",
  };
  const result = await txReadWrite<OutboxItem>(STORES.outbox, async (_tx, store) => {
    const s = store as IDBObjectStore;
    // Dedupe por idemKey: no encolar dos veces el MISMO intento (cinturón extra
    // anti-duplicados; complementa la idempotencia atómica del servidor).
    if (full.idemKey) {
      const existing = (await reqAsPromise<OutboxItem[]>(s.getAll())).find(
        (i) => i.idemKey === full.idemKey
      );
      if (existing) return existing;
    }
    const r = await reqAsPromise<IDBValidKey>(s.add(full));
    full.id = r as number;
    return full;
  });
  notify(CHANNELS.outbox);
  return result;
}

export async function listOutbox(filter?: {
  status?: OutboxStatus;
  kind?: string;
}): Promise<OutboxItem[]> {
  return txReadOnly<OutboxItem[]>(STORES.outbox, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const all = await reqAsPromise<OutboxItem[]>(s.getAll());
    return all.filter((i) => {
      if (filter?.status && i.status !== filter.status) return false;
      if (filter?.kind && i.kind !== filter.kind) return false;
      return true;
    });
  });
}

export async function getOutboxCount(filter?: {
  status?: OutboxStatus;
}): Promise<number> {
  const items = await listOutbox(filter);
  return items.length;
}

export async function getReadyToSend(now = Date.now()): Promise<OutboxItem[]> {
  return txReadOnly<OutboxItem[]>(STORES.outbox, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const all = await reqAsPromise<OutboxItem[]>(s.getAll());
    return all
      .filter(
        (i) =>
          (i.status === "pending" || i.status === "failed") &&
          i.nextAttemptAt <= now &&
          i.attempts < MAX_ATTEMPTS
      )
      .sort((a, b) => a.createdAt - b.createdAt);
  });
}

export async function markSending(id: number): Promise<void> {
  await txReadWrite(STORES.outbox, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const item = (await reqAsPromise(s.get(id))) as OutboxItem | undefined;
    if (!item) return;
    item.status = "sending";
    await reqAsPromise(s.put(item));
  });
  notify(CHANNELS.outbox);
}

export async function markCompleted(id: number): Promise<void> {
  await txReadWrite(STORES.outbox, async (_tx, store) => {
    const s = store as IDBObjectStore;
    await reqAsPromise(s.delete(id));
  });
  notify(CHANNELS.outbox);
}

export async function markFailed(
  id: number,
  error: string,
  opts: { conflict?: boolean } = {}
): Promise<void> {
  await txReadWrite(STORES.outbox, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const item = (await reqAsPromise(s.get(id))) as OutboxItem | undefined;
    if (!item) return;
    item.attempts = (item.attempts ?? 0) + 1;
    item.lastError = error;
    if (opts.conflict) {
      item.status = "conflict";
      item.nextAttemptAt = Number.MAX_SAFE_INTEGER; // bloqueado hasta intervención
    } else if (item.attempts >= MAX_ATTEMPTS) {
      item.status = "failed";
      item.nextAttemptAt = Number.MAX_SAFE_INTEGER;
    } else {
      item.status = "pending";
      item.nextAttemptAt = Date.now() + backoffFor(item.attempts);
    }
    await reqAsPromise(s.put(item));
  });
  notify(CHANNELS.outbox);
}

/**
 * Reagenda un item SIN quemar intentos. Para condiciones que no son culpa del
 * item: 425 (padre tmp aún sin drenar), 401/403 (sesión expirada — se recupera
 * al re-loguear). markFailed incrementa attempts y a los 8 lo mata; estos casos
 * deben poder esperar indefinidamente.
 */
export async function markDeferred(id: number, delayMs = 5_000, error?: string): Promise<void> {
  await txReadWrite(STORES.outbox, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const item = (await reqAsPromise(s.get(id))) as OutboxItem | undefined;
    if (!item) return;
    item.status = "pending";
    item.nextAttemptAt = Date.now() + delayMs;
    if (error !== undefined) item.lastError = error;
    await reqAsPromise(s.put(item));
  });
  notify(CHANNELS.outbox);
}

/**
 * Recupera items congelados en "sending": si el proceso murió a mitad de un
 * fetch (WebView matado, tab cerrada), el item quedó durable en "sending" y
 * getReadyToSend nunca lo vuelve a seleccionar. Se llama al boot del drainer,
 * cuando es seguro (ningún fetch de una sesión anterior sigue vivo).
 */
export async function recoverStuckSending(): Promise<number> {
  let n = 0;
  await txReadWrite(STORES.outbox, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const all = (await reqAsPromise(s.getAll())) as OutboxItem[];
    for (const item of all) {
      if (item.status !== "sending") continue;
      item.status = "pending";
      item.nextAttemptAt = Date.now();
      await reqAsPromise(s.put(item));
      n++;
    }
  });
  if (n > 0) notify(CHANNELS.outbox);
  return n;
}

/** Reactiva un item failed/conflict para drenaje. */
export async function retryItem(id: number): Promise<void> {
  await txReadWrite(STORES.outbox, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const item = (await reqAsPromise(s.get(id))) as OutboxItem | undefined;
    if (!item) return;
    item.attempts = 0;
    item.status = "pending";
    item.lastError = undefined;
    item.nextAttemptAt = Date.now();
    await reqAsPromise(s.put(item));
  });
  notify(CHANNELS.outbox);
}

export async function discardItem(id: number): Promise<void> {
  await markCompleted(id);
}

export async function clearAll(): Promise<void> {
  await txReadWrite(STORES.outbox, async (_tx, store) => {
    const s = store as IDBObjectStore;
    await reqAsPromise(s.clear());
  });
  notify(CHANNELS.outbox);
}
